import type { TranslationKeys } from '@signage-player/renderer-ui';
import type { FsDocument } from './webFullscreen';

/**
 * Install state of the web player. Chromium exempts documents running in an
 * installed app window from the autoplay-with-sound block, so "installed and
 * launched from the icon" is the permanent fix for the sound issue. The
 * beforeinstallprompt event fires once, early — the listener has to be
 * registered before boot() awaits anything or the prompt is lost.
 */

export type InstallState = 'standalone' | 'promptable' | 'installed-in-tab' | 'unavailable';
export type BrowserFamily = 'chromium' | 'firefox' | 'safari' | 'other';

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type RelatedApp = { platform?: string; url?: string; id?: string };
type NavigatorExt = Navigator & {
  standalone?: boolean;
  getInstalledRelatedApps?: () => Promise<RelatedApp[]>;
  userAgentData?: { brands?: Array<{ brand: string; version: string }> };
};

const nav = navigator as NavigatorExt;
const ua = navigator.userAgent;
// iPadOS Safari masquerades as a Mac; the touch points give it away.
export const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

let deferred: BeforeInstallPromptEvent | null = null;
let installedElsewhere = false;
let justInstalled = false;
const listeners = new Set<() => void>();

export function installWebInstall(): void {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    justInstalled = true;
    notify();
  });
  for (const q of ['(display-mode: standalone)', '(display-mode: fullscreen)']) {
    window.matchMedia?.(q)?.addEventListener?.('change', notify);
  }
  detectInstalledRelatedApp();
}

export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

// Chromium-only and unverified on Linux desktop: it must degrade silently.
function detectInstalledRelatedApp(): void {
  const probe = nav.getInstalledRelatedApps;
  if (typeof probe !== 'function') return;
  probe
    .call(nav)
    .then(apps => {
      installedElsewhere = apps.some(app => app.platform === 'webapp');
      notify();
    })
    .catch(() => {
      /* no support / rejected — stays false */
    });
}

export function installState(): InstallState {
  if (isAppWindow()) return 'standalone';
  if (deferred) return 'promptable';
  if (justInstalled || installedElsewhere) return 'installed-in-tab';
  return 'unavailable';
}

/** Must be called from a click handler: prompt() needs the user activation. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = deferred;
  if (!ev) return 'unavailable';
  deferred = null; // single-use: Chrome will not re-fire it for this document
  try {
    const shown = ev.prompt(); // called before any await — prompt() needs the click's activation
    await shown;
    const { outcome } = await ev.userChoice;
    return outcome;
  } catch {
    deferred = ev; // prompt() rejected before userChoice — keep the Install button alive
    return 'unavailable';
  } finally {
    notify();
  }
}

export function wasInstalledHere(): boolean {
  return justInstalled;
}

function fullscreenElement(): Element | null {
  return document.fullscreenElement || (document as FsDocument).webkitFullscreenElement || null;
}

// Launched from an app icon / Home Screen, not a browser tab.
export function isStandalone(): boolean {
  return (
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.matchMedia('(display-mode: fullscreen)').matches && !fullscreenElement())
  );
}

// F11/--kiosk also report display-mode: fullscreen; only Android honours the manifest's fullscreen mode for installed apps.
function isAppWindow(): boolean {
  return (
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.matchMedia('(display-mode: fullscreen)').matches && !fullscreenElement() && /Android/.test(ua))
  );
}

function detectFamily(): BrowserFamily {
  if (isIOS) return 'safari';
  if (nav.userAgentData?.brands?.some(b => /Chromium/i.test(b.brand))) return 'chromium';
  if (/Firefox|FxiOS/.test(ua)) return 'firefox';
  if (/Edg\/|Chrome\/|OPR\/|CriOS|EdgiOS/.test(ua)) return 'chromium';
  if (/Apple/.test(navigator.vendor ?? '') && /Safari\//.test(ua)) return 'safari';
  return 'other';
}

const family = detectFamily();

export function browserFamily(): BrowserFamily {
  return family;
}

/** The per-browser "make sound permanent" instruction for the current state. */
export function soundPermanentKey(): TranslationKeys {
  if (isIOS) return 'sound_permanent_ios';
  switch (family) {
    case 'chromium':
      switch (installState()) {
        case 'promptable':
          return 'sound_permanent_chromium_install';
        case 'installed-in-tab':
          return 'sound_permanent_chromium_launch';
        case 'standalone':
          return 'sound_permanent_chromium_standalone';
        default:
          return 'sound_permanent_chromium_manual';
      }
    case 'firefox':
      return 'sound_permanent_firefox';
    case 'safari':
      return 'sound_permanent_safari';
    default:
      return 'sound_permanent_other';
  }
}
